import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
// import { Link } from 'react-router-dom';
import { Calendar, momentLocalizer, Event as RBCEvent } from 'react-big-calendar';
import type { ToolbarProps } from 'react-big-calendar';
import moment from 'moment';
import { getEvents, AppEvent } from '../services/eventService';
import CalendarToolbar from './CalendarToolbar';
import Services from './Services';
import SponsorCarousel from './SponsorCarousel';
import { heroImages } from '../data/heroImages';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

const STORY_URL =
  'https://nwmf.org/small-town-big-heart-the-story-of-an-event-center-built-by-neighbors/';


interface CalendarEvent extends RBCEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: AppEvent;
}

const CalendarComponent: React.FC = () => {
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<AppEvent | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<'month' | 'week' | 'day'>('month');
  const [heroIndex, setHeroIndex] = useState(0);
  const calendarView = currentView === 'month' ? 'month' : 'agenda';

  const fetchEvents = useCallback(async () => {
    try {
      if (!hasLoaded) {
        setLoading(true);
      }
      setIsFetching(true);
      setError(null);
      const rangeStart = moment(currentDate).startOf('month').subtract(1, 'month').toDate();
      const rangeEnd = moment(currentDate).endOf('month').add(1, 'month').toDate();
      const eventData = await getEvents(rangeStart, rangeEnd);
      setEvents(eventData);
      setHasLoaded(true);
    } catch (error) {
      console.error('Error fetching events:', error);
      const message = error instanceof Error ? error.message : 'Unable to load calendar events right now.';
      setError(message);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [currentDate, hasLoaded]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 6500);

    return () => window.clearInterval(interval);
  }, []);

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event.resource);
  };

  const formatEvents = (): CalendarEvent[] => {
    return events.map(event => ({
      id: event.id,
      title: event.title,
      start: new Date(event.start_time),
      end: new Date(event.end_time),
      resource: event
    }));
  };

  const showError = Boolean(error && !events.length);

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-slides" aria-hidden="true">
          {heroImages.map((src, index) => (
            <div
              key={src}
              className={`hero-slide${index === heroIndex ? ' is-active' : ''}`}
            >
              <img src={src} alt="" className="hero-slide-img" />
            </div>
          ))}
        </div>
        <div className="hero-overlay" aria-hidden="true" />
        <div className="hero-content">
          <h1 className="hero-title">TDS Event Center</h1>
          <p className="hero-subtitle">
            We&apos;re close to finishing the building and need your help to cross the finish line.
            Join <Link to="/team200" className="hero-inline-link">Team 200</Link> or make a gift today.
          </p>
          <div className="hero-cta">
            <a
              href="https://secure.qgiv.com/for/friendsofthefair-communitybuilding/?utm_id=97758_v0_s00_e0_tv2_a1den1nmxgth1u"
              className="btn btn-primary btn-hero"
            >
              Donate Now
            </a>
            <Link to="/team200" className="btn btn-secondary btn-hero">
              Join Team 200
            </Link>
          </div>
        </div>
        <p className="hero-credit">
          Photos by{' '}
          <a
            className="hero-credit-name"
            href="https://tyrabphotography.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            TyraB Photography
          </a>
          <span className="hero-credit-sep" aria-hidden="true">·</span>
          Tables &amp; chairs by{' '}
          <a
            className="hero-credit-name"
            href="https://riversiderentalsfertile.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Riverside Rentals
          </a>
        </p>
      </div>

      {/* Scavenger Hunt Promo — temporarily disabled; re-enable for a future rollout
      <section className="game-promo">
        <div className="game-promo-content">
        <div className="game-promo-text">
          <span className="promo-badge">New</span>
            <img src="/PCFairLogo.png" alt="Polk County Fair" className="promo-logo" />
          <h2>Polk County Fair Scavenger Hunt</h2>
          <p>
            Join the daily hunt, solve the clue, and enter for the grand prize. Play one day or
            all five.
          </p>
            <div className="game-promo-actions">
              <Link to="/game" className="btn btn-primary">
                Play the Game 🎉
              </Link>
              <p className="game-promo-note">Daily clues from July 8–12 at noon CST.</p>
            </div>
          </div>
        </div>
      </section>
      */}

      <section className="story-feature" aria-labelledby="story-feature-title">
        <a
          className="story-card"
          href={STORY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="story-copy">
            <div className="story-meta">
              <span className="story-kicker">In the news</span>
              <time dateTime="2026-09-29">September 29, 2026</time>
            </div>
            <h2 id="story-feature-title">Small town, big heart</h2>
            <p className="story-dek">The story of an event center built by neighbors</p>
            <p className="story-excerpt">
              Northwest Minnesota Foundation on how Fertile neighbors came together and raised a
              $1.3 million campaign to build a year-round center at the Polk County Fairgrounds.
            </p>
            <div className="story-footer">
              <span className="story-source">Northwest Minnesota Foundation</span>
              <span className="story-cta">Read the story</span>
            </div>
          </div>
          <div className="story-visual">
            <img src="/story/event-center-exterior.jpg" alt="" />
          </div>
        </a>
      </section>

      {/* Services Section */}
      <Services />

      {/* Sponsor Carousel */}
      <SponsorCarousel />

      {/* Donate Section */}
      <section className="donate-section">
        <div className="donate-card">
          <h2>Help Us Finish the Building</h2>
          <p>
            We are close to completing the TDS Event Center and still need more donations to cross
            the finish line.
          </p>
          <p className="donate-story">
            <a href={STORY_URL} target="_blank" rel="noopener noreferrer">
              Read how neighbors came together
            </a>
          </p>
          <a
            className="contact-button contact-button-primary donate-button"
            href="https://secure.qgiv.com/for/friendsofthefair-communitybuilding/?utm_id=97758_v0_s00_e0_tv2_a1den1nmxgth1u"
          >
            Donate
          </a>
        </div>
      </section>

      {/* Calendar Section */}
      <div className="calendar-container">
        <div className="calendar-header">
          <h2>Upcoming Events</h2>
          <p className="calendar-subtitle">Browse our calendar and find the perfect date for your event</p>
          {isFetching && <p className="calendar-subtitle">Refreshing calendar…</p>}
          {showError && <p className="calendar-subtitle">{error}</p>}
        </div>

        <div className="calendar-wrapper">
          {loading ? (
            <div className="calendar-loading">
              <div className="calendar-spinner" aria-hidden="true" />
            </div>
          ) : (
            <Calendar<CalendarEvent, object>
              localizer={localizer}
              events={formatEvents()}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              onSelectEvent={handleSelectEvent}
              views={['month', 'agenda']}
              defaultView="month"
              view={calendarView}
              onView={(nextView) => {
                if (nextView === 'month') {
                  setCurrentView('month');
                } else {
                  setCurrentView('day');
                }
              }}
              length={currentView === 'week' ? 7 : 1}
              date={currentDate}
              onNavigate={(newDate) => setCurrentDate(newDate)}
              defaultDate={new Date()}
              components={{
                toolbar: (props: ToolbarProps<CalendarEvent, object>) => (
                  <CalendarToolbar
                    {...props}
                    viewMode={currentView}
                    onViewModeChange={setCurrentView}
                  />
                )
              }}
            />
          )}
        </div>
      </div>

      {selectedEvent && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{selectedEvent.title}</h3>
            <p>
              <strong>Description:</strong> {selectedEvent.description || 'No description'}
              {selectedEvent.location ? ` (Space: ${selectedEvent.location})` : ''}
            </p>
            <p><strong>Start:</strong> {moment(selectedEvent.start_time).format('MMMM Do YYYY, h:mm a')}</p>
            <p><strong>End:</strong> {moment(selectedEvent.end_time).format('MMMM Do YYYY, h:mm a')}</p>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setSelectedEvent(null);
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default CalendarComponent;
